/* eslint-disable @typescript-eslint/no-unused-vars */
import { useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { get } from "lodash";
import { useTranslation } from "react-i18next";

import { client } from "../../bit/board/hooks/useGraphQLClient";
import { useAppContext } from "../../bit/board";

import { QUERY_DIGITAL_RECEPTION } from "../../config/graphql/query";
import { useOfflineContext } from "../../context/Offline";

const BOARD_POLL_MINUTES = 5;
const METERED_BOARD_POLL_MINUTES = 30;

export const useQueryBoard = () => {
  const { screenId } = useAppContext();
  const [, { language }] = useTranslation();
  const { online, storeData, getData } = useOfflineContext();

  const queryClient = useQueryClient();

  const queryKey = ["digitalReception", { id: screenId, language, online }];

  const cached = queryClient.getQueryData<{
    digitalReception: IDigitalReception;
  }>(queryKey);

  const pollMs =
    (cached?.digitalReception?.metadata?.network?.metered
      ? METERED_BOARD_POLL_MINUTES
      : BOARD_POLL_MINUTES) *
    (60 * 1000);

  return useQuery(
    queryKey,
    async () => {
      try {
        const data = await client.request<{
          digitalReception: IDigitalReception;
        }>(QUERY_DIGITAL_RECEPTION, {
          id: screenId,
          language,
        });

        await storeData(`/app/config/${language}`, data);
        return data;
      } catch (error) {
        const config = await getData<{ digitalReception: IDigitalReception }>(
          `/app/config/${language}`,
        );
        return config;
      }
    },
    {
      enabled: !!screenId,
      staleTime: pollMs,
      refetchInterval: pollMs,
      keepPreviousData: true,
    },
  );
};

export function useClientPlan() {
  const { data } = useQueryBoard();

  return data?.digitalReception?.client?.plan;
}

export function useLogoUrl() {
  const { data } = useQueryBoard();

  return data?.digitalReception?.theme?.images?.logo?.absolutePath;
}

export function useLayout(): Partial<IDigitalReceptionLayout> {
  const { data, dataUpdatedAt } = useQueryBoard();

  // added to react to re-render when data is updated
  // this needs to be here for the preview to work in the CMS
  // dataUpdatedAt;

  return {
    welcomeMessage: get(
      data,
      `digitalReception.settings.layout.welcomeMessage`,
    ),
    checkin: get(data, "digitalReception.settings.layout.checkin"),
    integratedManualCheckin: get(
      data,
      "digitalReception.settings.layout.integratedManualCheckin",
    ),
    independentManualCheckin: get(
      data,
      "digitalReception.settings.layout.independentManualCheckin",
    ),
    checkout: get(data, "digitalReception.settings.layout.checkout"),
    manualCheckout: get(
      data,
      "digitalReception.settings.layout.manualCheckout",
    ),
    packageDelivery: get(data, "digitalReception.settings.layout.package"),
    foodDelivery: get(data, "digitalReception.settings.layout.food"),
    callReception: get(data, "digitalReception.settings.layout.callReception"),
    overview: get(data, "digitalReception.settings.layout.overview"),
    wifi: get(data, "digitalReception.settings.layout.wifi"),
    wayfinding: get(data, "digitalReception.settings.layout.wayfinding"),
    multiWayfinding: get(
      data,
      "digitalReception.settings.layout.multiWayfinding",
    ),
    bell: get(data, "digitalReception.settings.layout.bell"),
  };
}

export function useReceptionistPhone() {
  const { data } = useQueryBoard();

  return data?.digitalReception?.settings?.receptionistPhone?.primary;
}

export function useReceptionistTeamsEmail() {
  const { data } = useQueryBoard();

  return data?.digitalReception?.settings?.receptionistTeams?.email;
}

export const useDeliveryReceiver = () => {
  const { data } = useQueryBoard();

  return data?.digitalReception?.settings?.deliveryReceiver;
};

export const useOverview = () => {
  const { data } = useQueryBoard();

  return {
    code: data?.digitalReception?.settings?.overview?.code,
    openWithoutCode:
      data?.digitalReception?.settings?.overview?.openWithoutCode === true,
    fields: data?.digitalReception?.settings?.overview?.fields,
  };
};

export const useAnimationsEnabled = () => {
  const { data } = useQueryBoard();

  return data?.digitalReception?.settings?.animations?.enabled;
};

export const usePrintingEnabled = () => {
  const { data } = useQueryBoard();

  return !!data?.digitalReception?.settings?.printing?.enabled;
};

export const usePrinter = () => {
  const { data } = useQueryBoard();

  return data?.digitalReception?.settings?.printing?.printer;
};

export const usePrintingLabel = () => {
  const { data } = useQueryBoard();

  return data?.digitalReception?.settings?.printing?.label;
};

export const useWifiQrCode = () => {
  const { data } = useQueryBoard();

  return data?.digitalReception?.settings?.wifi?.qrCode;
};

export const useCallSettings = () => {
  const { data } = useQueryBoard();

  const call = data?.digitalReception?.settings?.call;

  return {
    ringDuration: call?.ringDuration || 30,
    maxDuration: call?.maxDuration || null,
  };
};

export const useClock = () => {
  const { data } = useQueryBoard();

  return data?.digitalReception?.settings?.clock;
};

export const useDigitalReceptionCountry = () => {
  const { data } = useQueryBoard();

  return data?.digitalReception?.building?.country;
};

export const useIdentificationCode = () => {
  const { integratedManualCheckin, independentManualCheckin } = useLayout();

  let manualCheckoutGuestCode = "";

  if (!!integratedManualCheckin?.enabled) {
    manualCheckoutGuestCode =
      integratedManualCheckin.registration?.fields.find(
        (field) => !!field.manualCheckoutIdentifier,
      )?.code || "";
  }

  if (!!independentManualCheckin?.enabled) {
    manualCheckoutGuestCode =
      independentManualCheckin.registration?.fields.find(
        (field) => !!field.manualCheckoutIdentifier,
      )?.code || "";
  }

  return manualCheckoutGuestCode;
};

export const useDigitalReceptionMetadata = () => {
  const { data } = useQueryBoard();

  return data?.digitalReception?.metadata || {};
};

export const useCheckFlowExtraButtons = (
  action?: "checkin" | "checkout",
): ICheckFlowExtraButton[] => {
  const metadata = useDigitalReceptionMetadata();

  return useMemo(() => {
    const flows = metadata?.uiOverrides?.home?.flows || {};

    const actions = action ? [action] : Object.keys(flows);

    return actions
      .flatMap(
        (key) => (flows[key]?.extraButtons || []) as ICheckFlowExtraButton[],
      )
      .filter(
        (button) =>
          !!button?.id &&
          button.enabled !== false &&
          (button.action || "call") === "call",
      );
  }, [metadata, action]);
};

export function useTeams() {
  const { data } = useQueryBoard();

  return data?.digitalReception?.teams;
}

export function useGoogleChat() {
  const { data } = useQueryBoard();

  return data?.digitalReception?.googleChat;
}

export const usePhotoCaptureSettings = () => {
  const { data } = useQueryBoard();

  const isPhotoCaptureEnabled =
    data?.digitalReception?.settings?.photoCapture?.enabled || false;

  return {
    isPhotoCaptureEnabled,
    isPhotoCaptureAllowedForEmployees:
      (isPhotoCaptureEnabled &&
        data?.digitalReception?.settings?.photoCapture?.employee?.enabled) ||
      false,
    isPhotoCaptureRequiredForEmployees:
      data?.digitalReception?.settings?.photoCapture?.employee?.required ||
      false,
    isPhotoCaptureAllowedForVisitors:
      (isPhotoCaptureEnabled &&
        data?.digitalReception?.settings?.photoCapture?.visitor?.enabled) ||
      false,
    isPhotoCaptureRequiredForVisitors:
      data?.digitalReception?.settings?.photoCapture?.visitor?.required ||
      false,
    isPhotoCaptureAllowedForMeetingAttendees:
      (isPhotoCaptureEnabled &&
        data?.digitalReception?.settings?.photoCapture?.meetingAttendee
          ?.enabled) ||
      false,
    isPhotoCaptureRequiredForMeetingAttendees:
      data?.digitalReception?.settings?.photoCapture?.meetingAttendee
        ?.required || false,
  };
};

export const useContactPreviewEnabled = () => {
  const { data } = useQueryBoard();

  return data?.digitalReception?.settings?.contactPreview?.enabled;
};

export function useCommunicationProvider() {
  const { data } = useQueryBoard();

  return data?.digitalReception?.communicationProvider || "twilio";
}

export const useTeamsVideoCallEnabled = () => {
  const { data } = useQueryBoard();

  return data?.digitalReception?.settings?.teamsVideoCall?.enabled || false;
};

export function useDigitalReceptionCode() {
  const { data } = useQueryBoard();

  return data?.digitalReception?.code || "";
}

export const useAccessSettings = () => {
  const { data } = useQueryBoard();

  const shellyPrefs = data?.digitalReception?.client.preferences?.shelly;
  const shellySettings = data?.digitalReception?.settings?.shelly;

  const tuyaPrefs = data?.digitalReception?.client.preferences?.tuya;
  const tuyaSettings = data?.digitalReception?.settings?.tuya;

  const isShellyEnabled = !!(
    shellyPrefs?.enabled &&
    shellySettings?.enabled &&
    shellySettings?.controller
  );

  const isTuyaEnabled = !!(
    tuyaPrefs?.enabled &&
    tuyaSettings?.enabled &&
    tuyaSettings?.controller
  );

  let activeAccess: "shelly" | "tuya" | null = null;

  if (isShellyEnabled) {
    activeAccess = "shelly";
  } else if (isTuyaEnabled) {
    activeAccess = "tuya";
  }

  const settingsMap = {
    shelly: shellySettings,
    tuya: tuyaSettings,
  };

  const settings = activeAccess ? settingsMap[activeAccess] : null;

  const isAccessEnabled = !!activeAccess;

  const isAccessEnabledForCheckin = !!(
    isAccessEnabled && settings?.checkin?.enabled
  );

  const isAccessEnabledForCheckout = !!(
    isAccessEnabled && settings?.checkout?.enabled
  );

  const allowedFor = (
    flow: "checkin" | "checkout",
    role: "employee" | "visitor" | "guest" | "meetingAttendee",
  ) =>
    !!(
      isAccessEnabled &&
      settings?.[flow]?.enabled &&
      settings?.[flow]?.[role]?.enabled
    );

  return {
    isAccessEnabled,
    activeAccess,
    accessController: settings?.controller,

    // check-in
    isAccessEnabledForCheckin,
    isAccessEnabledForAutoOpenForCheckin:
      isAccessEnabledForCheckin && settings?.checkin?.autoSwitch,
    isAccessAllowedForEmployeesForCheckin: allowedFor("checkin", "employee"),
    isAccessAllowedForVisitorsForCheckin: allowedFor("checkin", "visitor"),
    isAccessAllowedForGuestsForCheckin: allowedFor("checkin", "guest"),
    isAccessAllowedForMeetingAttendeesForCheckin: allowedFor(
      "checkin",
      "meetingAttendee",
    ),

    // check-out
    isAccessEnabledForCheckout,
    isAccessEnabledForAutoOpenForCheckout:
      isAccessEnabledForCheckout && settings?.checkout?.autoSwitch,
    isAccessAllowedForEmployeesForCheckout: allowedFor("checkout", "employee"),
    isAccessAllowedForVisitorsForCheckout: allowedFor("checkout", "visitor"),
    isAccessAllowedForGuestsForCheckout: allowedFor("checkout", "guest"),
    isAccessAllowedForMeetingAttendeesForCheckout: allowedFor(
      "checkout",
      "meetingAttendee",
    ),
  };
};

export const useHidScannerSettings = () => {
  const { data } = useQueryBoard();

  const scanner = data?.digitalReception?.settings?.scanner;

  const isHidScannerActive = scanner?.enabled || false;
  const isHidAutoScanEnabled = scanner?.autoScan?.enabled || false;
  const hidAutoScanType: ScannerActionType =
    scanner?.autoScan?.type || "CHECKIN_AND_CHECKOUT";
  const isOnlyQrScannerMode =
    isHidAutoScanEnabled && (scanner?.autoScan?.onlyQrScanner || false);

  return {
    isHidScannerActive,
    isHidAutoScanEnabled,
    hidAutoScanType,
    isOnlyQrScannerMode,
  };
};

export const useSocketScannerSettings = () => {
  const { data } = useQueryBoard();

  const config: IDigitalReceptionMetadataSocketScanner | undefined =
    data?.digitalReception?.metadata?.socketScanner;

  const socketUrl = config?.socketUrl;

  const isSocketScannerAllowedForCheckin = !!config?.checkinEnabled;
  const isSocketScannerAllowedForCheckout = !!config?.checkoutEnabled;

  const isSocketScannerActive =
    !!socketUrl &&
    (isSocketScannerAllowedForCheckin || isSocketScannerAllowedForCheckout);

  return {
    socketUrl,
    isSocketScannerAllowedForCheckin,
    isSocketScannerAllowedForCheckout,
    isSocketScannerActive,
  };
};

export const useDocumentScannerSettings = (): {
  isDocumentScannerEnabled: boolean;
  fields: Record<string, IDigitalReceptionDocumentScannerFieldMapping>;
} => {
  const { data } = useQueryBoard();

  const isDocumentScannerEnabled = !!(
    data?.digitalReception?.client?.preferences?.documentScanner?.enabled &&
    data?.digitalReception?.settings?.documentScanner?.enabled
  );

  const { enabled, ...fields } =
    data?.digitalReception?.settings?.documentScanner || {};

  return {
    isDocumentScannerEnabled,
    fields,
  };
};

export const useNfcScannerSettings = () => {
  const { data } = useQueryBoard();

  const isNfcScannerEnabled =
    data?.digitalReception?.settings?.nfc?.enabled || false;

  const nfcScannerSocketUrl = data?.digitalReception?.settings?.nfc?.socketUrl;

  const isNfcScannerActive = isNfcScannerEnabled && !!nfcScannerSocketUrl;

  return {
    isNfcScannerEnabled,
    nfcScannerSocketUrl,
    isNfcScannerActive,
  };
};

export default useQueryBoard;
